import React from 'react';
import { useNavigate } from 'react-router-dom';

const ConversationalSurveyBuilder = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">

        {/* Back button */}
        <button 
          onClick={() => navigate('/blog')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">
          HumanAI Is Re-Imagining Survey Design: A Conversational, AI-First Workspace
          </h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>January 2025</span>
            <span className="mx-2">•</span>
            <span>8 min read</span>
            <span className="mx-2">•</span>
            <span>Product Vision</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO, HumanAI</p>
            </div>
          </div>
        </header>

        {/* Blog content */}
        <div className="prose prose-lg prose-invert max-w-none space-y-6">

          <p className="text-lg">
            Drafting a rigorous questionnaire has always required two very different skill sets: <strong>research craftsmanship</strong>—writing clear, unbiased questions, planting skip logic, and balancing length—and <strong>interface wrangling</strong>—fighting with drag-and-drop builders, spreadsheet imports, and endless version files.
          </p>

          <p>
            Our vision is simple: collapse those jobs into a single, fluid conversation with an AI copilot—while still giving experts pixel-level control.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">What a Conversational Survey Builder Looks Like</h2>

          <div className="bg-gray-900 p-6 rounded-lg my-6">
            <div className="space-y-6">
              
              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Instant Drafting</h3>
                <p className="text-gray-300 mb-2"><strong>You Type:</strong> "Create a 7-question pulse survey on remote-work burnout."</p>
                <p className="text-gray-400 text-sm"><strong>What Happens:</strong> The model returns a structured JSON action list (ADD_QUESTION, etc.). The editor renders a complete draft—no copy/paste.</p>
              </div>

              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Bias & Clarity Scan</h3>
                <p className="text-gray-300 mb-2"><strong>You Type:</strong> "Check the survey for leading language."</p>
                <p className="text-gray-400 text-sm"><strong>What Happens:</strong> AI flags specific items, suggests neutral rewrites, and cites best-practice guidelines.</p>
              </div>

              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Logic Reasoning</h3>
                <p className="text-gray-300 mb-2"><strong>You Type:</strong> "If Q3 = 'No internet', skip to the end."</p>
                <p className="text-gray-400 text-sm"><strong>What Happens:</strong> Adds a skip-rule object to the survey schema and shows the new branch in a visual flowchart.</p>
              </div>

              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Variant Generator</h3>
                <p className="text-gray-300 mb-2"><strong>You Type:</strong> "Give me two plainer options for Q2."</p>
                <p className="text-gray-400 text-sm"><strong>What Happens:</strong> Produces side-by-side phrasings you can A/B test or swap in with one click.</p>
              </div>

              <div className="border-b border-gray-700 pb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Predictive UX (roadmap)</h3>
                <p className="text-gray-300 mb-2"><strong>You Type:</strong> "Will a 20-item survey hurt completion?"</p>
                <p className="text-gray-400 text-sm"><strong>What Happens:</strong> Simulates drop-off using industry benchmarks; flags questions to trim.</p>
              </div>

              <div className="pb-4">
                <h3 className="text-lg font-semibold text-white mb-2">One-Click Translation</h3>
                <p className="text-gray-300 mb-2"><strong>You Type:</strong> "Translate entire survey to Spanish—keep medical terms."</p>
                <p className="text-gray-400 text-sm"><strong>What Happens:</strong> Generates locale-aware text and toggles the editor to bilingual mode.</p>
              </div>

            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Why Structured AI Beats "ChatGPT-and-Paste"</h2>

          <ul>
            <li><strong>Action-Oriented Outputs</strong> – The model never responds with paragraphs you must sift through; it sends deterministic actions the UI can apply (and undo) instantly.</li>
            <li><strong>Full-State Prompts</strong> – Every call includes the current survey JSON plus recent chat, so the AI never "forgets" the skip logic you added five minutes ago.</li>
            <li><strong>Hard Guardrails</strong> – Ambiguous requests trigger clarifying questions, not creative hallucinations. Invalid actions fail loudly and are logged.</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Tangible Benefits</h2>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">70% Faster Drafts</h3>
          <p>
            Boilerplate screens, Likert scaffolds, and consent text appear in seconds—letting researchers focus on hypotheses.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Cleaner Data, Fewer Biases</h3>
          <p>
            Real-time language audits catch double-barreled, leading, or jargon-laden questions before launch day.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">No More Context Switching</h3>
          <p>
            Chat your intent, then fine-tune visually in the same window—one source of truth, zero exporting or reformatting.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Team Trust & Traceability</h3>
          <p>
            Every human or AI edit is version-tracked; you can roll back or inspect diffs just like code.
          </p>

          <h3 className="text-xl font-semibold text-white mt-8 mb-2">Scales Without Re-Architecting</h3>
          <p>
            The backend stays stateless: the client always ships the full survey context, so horizontal scaling is trivial.
          </p>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">On the Near-Term Road-Map</h2>

          <ul>
            <li><strong>Drop-Off Prediction & Length Optimizer</strong></li>
            <li><strong>Auto-Generated Test Panels</strong> (simulate 100 virtual respondents to stress-test logic and branching)</li>
            <li><strong>WCAG-AA Accessibility Analyzer</strong></li>
            <li><strong>CSV / Alchemer Import & PDF / JSON / CSV Export</strong></li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-12 mb-4 border-b border-gray-700 pb-1">Where We're Headed</h2>

          <p>
            Survey software shouldn't feel like word-processing in 1998. By turning questionnaire design into a live, iterative conversation—backed by deterministic, transparent AI—we free researchers to spend less time on radio-button plumbing and more time extracting insight.
          </p>

          <p className="text-lg font-semibold text-white">
            Stay tuned for the open beta, and let's make rigorous data collection as intuitive as saying what you need.
          </p>

          <p className="mt-6 font-semibold">
            <a href="#request-access" className="text-pink-400 underline hover:text-pink-300">Request early access</a> to be the first to experience the future of survey design.
          </p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>

          <p className="mt-6 font-semibold">— Sharif From HumanAI</p>

        </div>
      </div>
    </div>
  );
};

export default ConversationalSurveyBuilder; 